import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  SaveOutlined as SaveIcon,
  DeleteOutline as DeleteIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { Box, TextField, Typography, Dialog, Tooltip, Stack, Button } from '@mui/material';
import { Switch } from 'app/components/v2';
import { getPolicyById, manageProtectionAspects } from 'app/store/features/privacyPersistedSlice';
import { deleteDataClassLevel, updateNameOrDescDataColumns } from 'app/store/features/privacySlice';
import { updateUserSettings } from 'app/store/features/userSettingsSlice';
import { ColorsV2 } from 'app/constants/color';
import { MuiColorInput } from 'mui-color-input';
import { TinyColor } from "@ctrl/tinycolor";
import { Buttons } from 'app/constants/sizing';

const EditDataClassModal = ({
  isOpen,
  setIsOpen,
  columnData,
  setColumnData,
  columns,
  setColumns,
}) => {

  const DEFAULT_INFOCLASS_COLOR = '#ffffff';
  const { t } = useTranslation('translation', { keyPrefix: 'dataclass_modal' });

  const [protectionAspect, setProtectionAspect] = useState(columnData?.protectionAspects);
  const [infoClassColor, setInfoClassColor] = useState(columnData?.color || DEFAULT_INFOCLASS_COLOR);

  const dispatch = useDispatch();

  const togglePopup = () => {
    setIsOpen({ ...isOpen, privacyModalOpen: false });
  };

  const handleChange = e => {
    setColumnData({
      ...columnData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSave = id => {
    dispatch(
      manageProtectionAspects({
        id,
        data: { protectionAspects: protectionAspect },
      })
    );

    const generateGradient = (baseColor, intensity = 10, direction = 'lighten') => {
      const color = new TinyColor(baseColor);
      
      // Slightly adjust brightness and saturation
      const fadedColor = direction === 'darken' ? color.darken(intensity) : color.lighten(intensity);
      return fadedColor.toHexString();
    };
    
    const infoObjectColors = {};
    columnData?.items?.map((infoObject, index) => {
      const hsl = new TinyColor(infoClassColor).toHsl();
      const baseColor = ({ h: hsl.h, s: hsl.s, l: 0.3 })

      const intensity = (0.5 / columnData?.items?.length) * (index + 1) * 100;
      infoObjectColors[infoObject?.id || 'object_id_missing'] = generateGradient(baseColor, intensity);
    });
    
    dispatch(
      updateUserSettings({ key: 'chartColors', value: { [id]: infoClassColor, ...infoObjectColors } })
    );
    dispatch(
      updateNameOrDescDataColumns({
        obj: { name: columnData?.name, description: columnData?.describe },
        id,
      })
    ).then(() => {
      dispatch(getPolicyById({ id: localStorage.getItem('policyType'), readOnly: true }));
    });
    setColumns(prevColumns => ({
      ...prevColumns,
      [id]: {
        ...prevColumns[id],
        name: columnData?.name,
        describe: columnData?.describe,
        protectionAspects: protectionAspect,
      },
    }));
    setIsOpen({ ...isOpen, privacyModalOpen: false });
  };

  const handleDelete = id => {
    const updatedData = JSON.parse(JSON.stringify(columns));
    const elements = updatedData[id]?.items;
    delete updatedData[id];
    if (updatedData['id-class-level-notcategorized']) {
      updatedData['id-class-level-notcategorized'].items = [
        ...updatedData['id-class-level-notcategorized'].items,
        ...elements,
      ];
      setColumns(updatedData);
    }
    dispatch(deleteDataClassLevel(id));
    setIsOpen({ ...isOpen, privacyModalOpen: false });
  };  

  const handleCheckboxChange = (event, subItemId) => {
    const updatedColumnData = columnData.protectionAspects.map(item => {
      return {
        ...item,
        restrictions: item.restrictions.map(subItems => {
          if (subItems.id === subItemId) {
            return {
              ...subItems,
              enforced: event.target.checked,
            };
          }
          return subItems;
        }),
      };
    });

    setColumnData({ ...columnData, protectionAspects: updatedColumnData });
    setProtectionAspect(updatedColumnData);
  };

  return (
    <>
      <Dialog maxWidth={'100%'} open={true} onClose={togglePopup}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            minWidth: '25vw',
            padding: '20px',
            background: ColorsV2.secondary,
            overflowY: 'auto',
            overflowX: 'hidden',
            backgroundColor: ColorsV2.secondary,
            border: `2px solid ${ColorsV2.outline}`,
            borderRadius: '0px',
          }}
        >
          <Typography variant='h5' sx={{ color: ColorsV2.white }}>{t('title')}</Typography>
          <Stack>
            <Typography variant='body1' sx={{ color: ColorsV2.textInfo }}>{t('name')}</Typography>
            <TextField
              noValidate
              id='name'
              value={columnData?.name}
              name='name'
              onChange={e => handleChange(e)}
              size='small'
              sx={{
                ...Buttons.baseStyle.textFieldLowPriority,
                width: "100%",
                "& .MuiInputBase-root": {
                  width: "100%",
                },
              }}
            />
          </Stack>
          <Stack>
            <Typography variant='body1' sx={{ color: ColorsV2.textInfo }}>{t('description')}</Typography>
            <TextField
              id='fullWidth'
              minRows={1}
              maxRows={5}
              value={columnData.describe}
              name='describe'
              size='small'
              multiline
              onChange={e => handleChange(e)}
              // style={{
              //   width: 'calc(100% - 20px)',
              //   fontFamily: 'sans-serif',
              //   fontSize: '0.6em',
              //   resize: 'none',
              //   backgroundColor: ColorsV2.primary,
              //   borderColor: ColorsV2.outline,
              //   color: ColorsV2.white,
              //   padding: 10,
              //   lineHeight: 1.5,
              //   fontWeight: 'normal',
              // }}
              sx={{
                ...Buttons.baseStyle.textFieldMultiline,
                width: '100%',
                "& .MuiInputBase-root": {
                  width: "100%",
                },
              }}
            />
          </Stack>
          <Stack>
            <Typography variant='body1' sx={{ color: ColorsV2.textInfo }}>
              {t('color_title')}
            </Typography>
            <Tooltip title={t('color_description')}>
              <MuiColorInput
              value={infoClassColor}
              onChange={(newColor) => setInfoClassColor(newColor)}
              sx={{
                background: ColorsV2.primary,
                borderRadius: '4px',
                '& input': {
                  color: ColorsV2.textInfo,
                  fontSize: '0.875rem'
                }
              }}
              />
            </Tooltip>
          </Stack>
          <Stack>
          <Typography variant='body1' sx={{ color: ColorsV2.textInfo, borderBottom: `1px solid ${ColorsV2.outline}` }}>
            {t('applicable')}
          </Typography>
          {columnData?.protectionAspects.map((item, index) => (
            <Box key={index} sx={{ mt: '10px' }}>
              <Typography variant='body1' sx={{ color: ColorsV2.white }}>
                {item?.name}
              </Typography>
              <Box sx={{ pl: '10px' }}>
                {item?.restrictions?.map((subItems, index) => (
                  <Box
                    key={index}
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      marginBlock: '5px',
                    }}
                  >
                    <Typography
                    variant='body2'
                      sx={{ color: ColorsV2.textInfo, }}
                    >
                      {subItems?.name}
                    </Typography>
                    <Switch
                      checked={subItems?.enforced}
                      handlechange={event => handleCheckboxChange(event, subItems?.id)}
                    />
                  </Box>
                ))}
              </Box>
            </Box>
          ))}
          </Stack>
          <Box
            sx={{
              display: 'flex',
              width: '100%',
              justifyContent: 'space-around',
              alignItems: 'center',
            }}
          >
            <Button
              name='IconButton'
              onClick={() => {
                setIsOpen({ ...isOpen, privacyModalOpen: false });
              }}
              sx={{ ...Buttons.baseStyle.primaryButtonLowPriority }}
              startIcon={<CloseIcon sx={{ color: 'white', fontSize: '0.7em' }} />}
            >
              {t('exit')}
            </Button>
            {columnData.name !== 'Not categorized' && (
              <Button
                name='IconButton'
                onClick={() => handleDelete(columnData?.id)}
                sx={{ ...Buttons.baseStyle.primaryButtonLowPriority }}
                startIcon={<DeleteIcon sx={{ color: 'white', fontSize: '0.7em' }} />}
              >
                {t('delete')}
              </Button>
            )}
            <Button
              name='IconButton'
              onClick={() => handleSave(columnData.id)}
              startIcon={<SaveIcon sx={{ color: 'white', fontSize: '0.7em' }} />}
              sx={{ ...Buttons.baseStyle.primaryButtonHighPriority }}
            >
              {t('save')} {/* // Change translation to update instead? */}
            </Button>
          </Box>
        </Box>
      </Dialog>
    </>
  );
};

export default EditDataClassModal;
